import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Tag, GraduationCap, Calendar, Users, TrendingUp, Mail, ArrowRight } from "lucide-react";
import { apiAuthGet } from "@/lib/api";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    apiAuthGet("/admin/stats").then(setStats).catch((e) => setErr(e.message));
  }, []);

  if (err) return <div className="text-red-400" data-testid="dashboard-error">{err}</div>;
  if (!stats) return <div className="text-slate-400" data-testid="dashboard-loading">Lade Daten …</div>;

  const tiles = [
    { label: "Anfragen gesamt", value: stats.contacts.total, icon: Mail, color: "from-emerald-400 to-emerald-600", to: "/admin/inbox" },
    { label: "Neue (ungelesen)", value: stats.contacts.new, icon: Inbox, color: "from-amber-400 to-amber-600", to: "/admin/inbox", highlight: stats.contacts.new > 0 },
    { label: "Letzte 7 Tage", value: stats.contacts.last_7_days, icon: TrendingUp, color: "from-blue-400 to-blue-600", to: "/admin/inbox" },
  ];

  const content = [
    { label: "TM7 Angebote", value: stats.content.angebote, icon: Tag, to: "/admin/angebote" },
    { label: "Einsteiger-Programm", value: stats.content.einsteiger, icon: GraduationCap, to: "/admin/einsteiger" },
    { label: "Kochkurse", value: stats.content.kochkurse, icon: Calendar, to: "/admin/kochkurse" },
    { label: "Comeback-Termine", value: stats.content.comeback, icon: Users, to: "/admin/comeback" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-bold mb-1">Übersicht</div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white font-[Outfit]">Hallo Jennifer 👋</h1>
        <p className="text-slate-400 mt-2 text-sm">Hier siehst du auf einen Blick, was bei deiner Website los ist.</p>
      </div>

      {/* Kontakt-Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        {tiles.map((t) => (
          <Link key={t.label} to={t.to} data-testid={`stat-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
            className={`group relative rounded-2xl p-5 backdrop-blur-xl bg-white/5 border transition-all hover:-translate-y-0.5 hover:shadow-xl ${
              t.highlight ? "border-amber-400/40 shadow-lg shadow-amber-500/10" : "border-white/10"
            }`}>
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${t.color} flex items-center justify-center shadow-lg`}>
                <t.icon className="w-5 h-5 text-white" />
              </div>
              <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all" />
            </div>
            <div className="text-3xl font-extrabold text-white font-[Outfit] leading-none">{t.value}</div>
            <div className="text-xs text-slate-400 mt-1.5 uppercase tracking-wider font-semibold">{t.label}</div>
          </Link>
        ))}
      </div>

      {/* Content-Stats */}
      <div>
        <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-slate-400 mb-3">Content-Bereiche</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {content.map((c) => (
            <Link key={c.label} to={c.to} data-testid={`content-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
              className="group rounded-xl p-4 bg-white/5 border border-white/10 hover:border-emerald-400/40 hover:bg-white/10 transition-all flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center text-emerald-300">
                <c.icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{c.label}</div>
                <div className="text-xl font-bold text-white font-[Outfit]">{c.value} Einträge</div>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all" />
            </Link>
          ))}
        </div>
      </div>

      <div className="rounded-2xl p-5 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20">
        <div className="text-xs uppercase tracking-wider text-emerald-300 font-bold mb-1">Tipp</div>
        <p className="text-slate-300 text-sm">
          Über die Sidebar links kannst du Angebote, Kochkurs-Termine und Comeback-Daten anpassen.
          Änderungen sind sofort live auf deiner Website sichtbar.
        </p>
      </div>
    </div>
  );
}
